import { useState } from 'react';
import html2pdf from 'html2pdf.js';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../lib/toast';
import { ClipboardList, X, Printer, FileDown, Search, ArrowLeft } from 'lucide-react';

const todayLabel = () => new Date().toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

export const WorkOrderSheet = ({ products, onClose }) => {
  const [step, setStep] = useState('select');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [title, setTitle] = useState('ORDEN DE TRABAJO - PRODUCCIÓN');
  const [notes, setNotes] = useState('');

  const toggle = (p) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[p.processed_id]) delete next[p.processed_id];
      else next[p.processed_id] = { name: p.processed_name, unit: p.unit || '', qty: '1' };
      return next;
    });
  };

  const setQty = (id, qty) => setSelected(prev => ({ ...prev, [id]: { ...prev[id], qty } }));

  const num = (v) => parseFloat(String(v).replace(',', '.'));
  const rows = Object.entries(selected).map(([id, v]) => ({ id, ...v }));
  const missingQty = rows.filter(r => !(num(r.qty) > 0));
  const canPreview = rows.length > 0 && missingQty.length === 0;

  const filtered = products.filter(p => p.processed_name.toLowerCase().includes(search.toLowerCase()));

  const sheetHtml = () => `
    <div style="font-family: Arial, sans-serif; padding: 24px; color: #111;">
      <div style="display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 3px solid #111; padding-bottom: 8px;">
        <div>
          <div style="font-size: 22px; font-weight: 800; letter-spacing: 1px;">${title}</div>
          <div style="font-size: 12px; color:#555; text-transform: capitalize;">${todayLabel()}</div>
        </div>
        <div style="font-size: 12px; text-align:right;">
          <div>Turno: ______________</div>
          <div style="margin-top:4px;">Encargado: ______________</div>
        </div>
      </div>
      <table style="width:100%; border-collapse: collapse; margin-top: 16px; font-size: 13px;">
        <thead>
          <tr style="background:#111; color:#fff;">
            <th style="padding:8px; text-align:left; width:36px;">#</th>
            <th style="padding:8px; text-align:left;">Elaborado</th>
            <th style="padding:8px; text-align:right; width:110px;">Cantidad</th>
            <th style="padding:8px; text-align:left; width:70px;">Unidad</th>
            <th style="padding:8px; text-align:center; width:60px;">Hecho</th>
            <th style="padding:8px; text-align:left; width:150px;">Responsable</th>
            <th style="padding:8px; text-align:left; width:80px;">Hora</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map((r, i) => `
            <tr style="border-bottom: 1px solid #bbb; height: 38px;">
              <td style="padding:8px; color:#666;">${i + 1}</td>
              <td style="padding:8px; font-weight: 700; font-size: 14px;">${r.name}</td>
              <td style="padding:8px; text-align:right; font-weight:700; font-size: 15px;">${num(r.qty).toLocaleString('es-MX')}</td>
              <td style="padding:8px;">${r.unit}</td>
              <td style="padding:8px; text-align:center;"><span style="display:inline-block; width:18px; height:18px; border:2px solid #111; border-radius:3px;"></span></td>
              <td style="padding:8px; border-left:1px dashed #ccc;"></td>
              <td style="padding:8px; border-left:1px dashed #ccc;"></td>
            </tr>`).join('')}
        </tbody>
      </table>
      ${notes ? `<div style="margin-top:16px; font-size: 13px; border:1px solid #bbb; padding:10px; border-radius:6px;"><b>Notas:</b> ${notes}</div>` : ''}
      <div style="margin-top: 28px; display:flex; justify-content:space-between; font-size: 12px; color:#333;">
        <div>Firma encargado: ______________________</div>
        <div>Revisó: ______________________</div>
      </div>
    </div>`;

  const downloadPdf = async () => {
    const container = document.createElement('div');
    container.innerHTML = sheetHtml();
    document.body.appendChild(container);
    try {
      await html2pdf().set({
        margin: 8,
        filename: `orden_trabajo_produccion_${new Date().toISOString().slice(0, 10)}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { format: 'letter', orientation: 'portrait' }
      }).from(container).save();
      toast.success('Orden de trabajo descargada');
    } catch (e) {
      toast.error('Error al generar PDF');
    } finally {
      document.body.removeChild(container);
    }
  };

  const printSheet = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Orden de trabajo</title></head><body>${sheetHtml()}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="work-order-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-accent" />
            {step === 'select' ? 'Orden de Trabajo · Elegir elaborados' : 'Orden de Trabajo · Vista previa'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="work-order-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {step === 'select' ? (
          <>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título de la hoja" data-testid="work-order-title-input" />
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notas para el equipo (opcional)" data-testid="work-order-notes-input" />
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input className="pl-9" placeholder="Buscar elaborado..." value={search} onChange={e => setSearch(e.target.value)} data-testid="work-order-search-input" />
              </div>
              <div className="border rounded-xl divide-y max-h-[48vh] overflow-y-auto">
                {filtered.map(p => {
                  const sel = selected[p.processed_id];
                  return (
                    <div key={p.processed_id} className={`flex items-center gap-3 px-4 py-2.5 ${sel ? 'bg-orange-50' : ''}`} data-testid={`work-order-row-${p.processed_id}`}>
                      <input type="checkbox" checked={!!sel} onChange={() => toggle(p)} className="w-4 h-4 accent-orange-500" data-testid={`work-order-check-${p.processed_id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.processed_name}</p>
                        <p className="text-xs text-gray-500">Stock: {parseFloat(p.current_stock || 0).toFixed(2)} {p.unit}</p>
                      </div>
                      {sel && (
                        <div className="flex items-center gap-2">
                          <Input type="text" inputMode="decimal" value={sel.qty} onChange={e => setQty(p.processed_id, e.target.value)} placeholder="Cant." className={`w-24 h-9 text-right ${num(sel.qty) > 0 ? '' : 'border-red-400'}`} data-testid={`work-order-qty-${p.processed_id}`} />
                          <span className="text-sm text-gray-500 w-10">{p.unit}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && <p className="p-6 text-center text-gray-500 text-sm">Sin resultados</p>}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600" data-testid="work-order-selected-count">
                {rows.length === 0
                  ? 'Marca al menos un elaborado para continuar'
                  : missingQty.length > 0
                    ? `Falta cantidad en: ${missingQty.map(r => r.name).join(', ')}`
                    : `${rows.length} elaborado(s) seleccionado(s)`}
              </span>
              <Button onClick={() => setStep('preview')} disabled={!canPreview} className="bg-accent hover:bg-accent/90 text-white shrink-0" data-testid="work-order-preview-btn">
                Ver vista previa
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 overflow-y-auto flex-1 bg-gray-100">
              <div className="bg-white shadow rounded-lg mx-auto" style={{ maxWidth: 820 }} dangerouslySetInnerHTML={{ __html: sheetHtml() }} data-testid="work-order-preview" />
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep('select')} data-testid="work-order-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Editar</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={printSheet} data-testid="work-order-print-btn"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
                <Button onClick={downloadPdf} className="bg-accent hover:bg-accent/90 text-white" data-testid="work-order-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> Descargar PDF</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
